// Animated radial-gradient "liquid" shader behind button text. Adapted
// from the 21st.dev "button-1" component (ui-layouts, MIT). Reworked for
// Vite + relative imports, with MTG's WUBRG mana palette instead of the
// demo's all-indigo stops. The gradient stops animate through four
// predefined configurations forward and back, so the surface reads as a
// slowly-shifting foil card.
//
// Usage:
//   <LiquidButton onClick={...}>Build your first deck</LiquidButton>
//   <LiquidButton palette={MANA_U}>Run playtest</LiquidButton>
//
// Pure CSS would struggle with the stop-count animation this component
// does, which is why motion ships with it. Kept the SVG structure from
// the source so future palette updates only touch COLORS.

import type React from "react";
import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "../../lib/cn";

type ColorKey =
  | "color1" | "color2" | "color3" | "color4" | "color5"
  | "color6" | "color7" | "color8" | "color9" | "color10"
  | "color11" | "color12" | "color13" | "color14" | "color15"
  | "color16" | "color17";

export type Colors = Record<ColorKey, string>;

// WUBRG palette — MTG's five-color wheel swept through a single button,
// with DEEP BLUE as the base instead of black. color1 / color11 serve as
// the interstitial "darkness between colors" and both use dark blue so
// the button reads unmistakably as Island-themed even when the brighter
// hues haven't rotated into view. The sweep then flows blue → red →
// green → blue with the brights punctuating the transitions.
export const MANA_WUBRG: Colors = {
  color1:  "#0a1530",   // deep blue base (was near-black)
  color2:  "#1e3a6b",   // U mid — island
  color3:  "#7fa7cd",   // U bright
  color4:  "#c8d8ec",   // pale blue highlight (was paper-white W)
  color5:  "#b3cfe6",   // U very pale (was W shadow)
  color6:  "#5a1c12",   // R deep
  color7:  "#bd685a",   // R bright
  color8:  "#1e3b1e",   // G deep — forest
  color9:  "#6a9a6a",   // G mid
  color10: "#a1c2a1",   // G bright
  color11: "#071226",   // deeper blue base (was near-black)
  color12: "#7fa7cd",   // U bright reprise
  color13: "#1e3a6b",   // U mid reprise
  color14: "#e09a8e",   // R pale
  color15: "#6a9a6a",   // G bright reprise
  color16: "#1e3a6b",   // U mid (was B neutral)
  color17: "#2d4966",   // U deep edge (was B edge)
};

// Single-color palettes for per-button theming (Blue = knowledge/draw,
// Red = combat/playtest, Green = growth/upload). Each keeps a dark base
// but sweeps through just one mana color family.
export const MANA_U: Colors = {
  ...MANA_WUBRG,
  color2: "#2d4966", color3: "#7fa7cd", color6: "#2d4966",
  color7: "#4a6f94", color8: "#1c2e40", color9: "#4a6f94",
  color10: "#b3cfe6", color13: "#1e3b66", color15: "#7fa7cd",
  color16: "#2d4966",
};

export const MANA_R: Colors = {
  ...MANA_WUBRG,
  color2: "#5a1c12", color3: "#bd685a", color6: "#5a1c12",
  color7: "#8a3020", color8: "#3a1008", color9: "#8a3020",
  color10: "#e09a8e", color13: "#4a1810", color15: "#bd685a",
  color16: "#5a1c12",
};

export const MANA_G: Colors = {
  ...MANA_WUBRG,
  color2: "#1e3b1e", color3: "#6a9a6a", color6: "#1e3b1e",
  color7: "#3d6a3d", color8: "#14281a", color9: "#3d6a3d",
  color10: "#a1c2a1", color13: "#1e3b1e", color15: "#6a9a6a",
  color16: "#1e3b1e",
};

const svgOrder = ["svg1", "svg2", "svg3", "svg4", "svg3", "svg2", "svg1"] as const;
type SvgKey = (typeof svgOrder)[number];
type Stop = { offset: number; stopColor: string };
type SvgState = { gradientTransform: string; stops: Stop[] };
type SvgStates = Record<SvgKey, SvgState>;

function createStopsArray(
  svgStates: SvgStates,
  order: readonly SvgKey[],
  maxStops: number,
): Stop[][] {
  const stopsArray: Stop[][] = [];
  for (let i = 0; i < maxStops; i++) {
    const stopConfigurations = order.map((svgKey) => {
      const svg = svgStates[svgKey];
      return svg.stops[i] ?? svg.stops[svg.stops.length - 1]!;
    });
    stopsArray.push(stopConfigurations);
  }
  return stopsArray;
}

function GradientSvg({
  className,
  isHovered,
  colors,
  gradientId,
}: {
  className: string;
  isHovered: boolean;
  colors: Colors;
  gradientId: string;
}) {
  const svgStates: SvgStates = {
    svg1: {
      gradientTransform: "translate(287.5 280) rotate(-29.0546) scale(689.807 1000)",
      stops: [
        { offset: 0, stopColor: colors.color1 },
        { offset: 0.188423, stopColor: colors.color2 },
        { offset: 0.260417, stopColor: colors.color3 },
        { offset: 0.328792, stopColor: colors.color4 },
        { offset: 0.328892, stopColor: colors.color5 },
        { offset: 0.328992, stopColor: colors.color1 },
        { offset: 0.442708, stopColor: colors.color6 },
        { offset: 0.537556, stopColor: colors.color7 },
        { offset: 0.631738, stopColor: colors.color1 },
        { offset: 0.725645, stopColor: colors.color8 },
        { offset: 0.817779, stopColor: colors.color9 },
        { offset: 0.84375, stopColor: colors.color10 },
        { offset: 0.90569, stopColor: colors.color1 },
        { offset: 1, stopColor: colors.color11 },
      ],
    },
    svg2: {
      gradientTransform: "translate(126.5 418.5) rotate(-64.756) scale(533.444 773.324)",
      stops: [
        { offset: 0, stopColor: colors.color1 },
        { offset: 0.104167, stopColor: colors.color12 },
        { offset: 0.182292, stopColor: colors.color13 },
        { offset: 0.28125, stopColor: colors.color1 },
        { offset: 0.328792, stopColor: colors.color4 },
        { offset: 0.328892, stopColor: colors.color5 },
        { offset: 0.453125, stopColor: colors.color6 },
        { offset: 0.515625, stopColor: colors.color7 },
        { offset: 0.631738, stopColor: colors.color1 },
        { offset: 0.692708, stopColor: colors.color8 },
        { offset: 0.75, stopColor: colors.color14 },
        { offset: 0.817708, stopColor: colors.color9 },
        { offset: 0.869792, stopColor: colors.color10 },
        { offset: 1, stopColor: colors.color1 },
      ],
    },
    svg3: {
      gradientTransform: "translate(264.5 339.5) rotate(-42.3022) scale(946.451 1372.05)",
      stops: [
        { offset: 0, stopColor: colors.color1 },
        { offset: 0.188423, stopColor: colors.color2 },
        { offset: 0.307292, stopColor: colors.color1 },
        { offset: 0.328792, stopColor: colors.color4 },
        { offset: 0.328892, stopColor: colors.color5 },
        { offset: 0.442708, stopColor: colors.color15 },
        { offset: 0.537556, stopColor: colors.color16 },
        { offset: 0.631738, stopColor: colors.color1 },
        { offset: 0.725645, stopColor: colors.color17 },
        { offset: 0.817779, stopColor: colors.color9 },
        { offset: 0.84375, stopColor: colors.color10 },
        { offset: 0.90569, stopColor: colors.color1 },
        { offset: 1, stopColor: colors.color11 },
      ],
    },
    svg4: {
      gradientTransform: "translate(860.5 420) rotate(-153.984) scale(957.528 1388.11)",
      stops: [
        { offset: 0.109375, stopColor: colors.color11 },
        { offset: 0.171875, stopColor: colors.color2 },
        { offset: 0.260417, stopColor: colors.color13 },
        { offset: 0.328792, stopColor: colors.color4 },
        { offset: 0.328892, stopColor: colors.color5 },
        { offset: 0.328992, stopColor: colors.color1 },
        { offset: 0.442708, stopColor: colors.color6 },
        { offset: 0.515625, stopColor: colors.color7 },
        { offset: 0.631738, stopColor: colors.color1 },
        { offset: 0.692708, stopColor: colors.color8 },
        { offset: 0.817708, stopColor: colors.color9 },
        { offset: 0.869792, stopColor: colors.color10 },
        { offset: 1, stopColor: colors.color11 },
      ],
    },
  };

  const maxStops = Math.max(...Object.values(svgStates).map((s) => s.stops.length));
  const stopsAnimationArray = createStopsArray(svgStates, svgOrder, maxStops);
  const gradientTransform = svgOrder.map((k) => svgStates[k].gradientTransform);

  const variants = {
    hovered: {
      gradientTransform,
      transition: { duration: 293, repeat: Infinity, ease: "linear" as const },
    },
    notHovered: {
      gradientTransform,
      transition: { duration: 98, repeat: Infinity, ease: "linear" as const },
    },
  };

  return (
    <svg
      className={className}
      width="1030"
      height="280"
      viewBox="0 0 1030 280"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid slice"
    >
      <rect width="1030" height="280" rx="140" fill={`url(#${gradientId})`} />
      <defs>
        <motion.radialGradient
          id={gradientId}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          animate={isHovered ? variants.hovered : variants.notHovered}
        >
          {stopsAnimationArray.map((stopConfigs, index) => (
            <AnimatePresence key={index}>
              <motion.stop
                initial={{
                  offset: stopConfigs[0]!.offset,
                  stopColor: stopConfigs[0]!.stopColor,
                }}
                animate={{
                  offset: stopConfigs.map((c) => c.offset),
                  stopColor: stopConfigs.map((c) => c.stopColor),
                }}
                transition={{ duration: 0, ease: "linear", repeat: Infinity }}
              />
            </AnimatePresence>
          ))}
        </motion.radialGradient>
      </defs>
    </svg>
  );
}

function Liquid({
  isHovered,
  colors,
  gradientId,
}: {
  isHovered: boolean;
  colors: Colors;
  gradientId: string;
}) {
  return (
    <>
      {Array.from({ length: 7 }).map((_, index) => (
        <div
          key={index}
          className={cn(
            "absolute",
            index < 3 ? "w-[443px] h-[121px]" : "w-[756px] h-[207px]",
            index === 0 && "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 mix-blend-difference",
            index === 1 && "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[164.971deg] mix-blend-difference",
            index === 2 && "top-1/2 left-1/2 -translate-x-[53%] -translate-y-[53%] rotate-[-11.61deg] mix-blend-difference",
            index === 3 && "top-1/2 left-1/2 -translate-x-1/2 -translate-y-[57%] rotate-[-179.012deg] mix-blend-difference",
            index === 4 && "top-1/2 left-1/2 -translate-x-[57%] -translate-y-1/2 rotate-[-29.722deg] mix-blend-difference",
            index === 5 && "top-1/2 left-1/2 -translate-x-[62%] -translate-y-[24%] rotate-[160.227deg] mix-blend-difference",
            index === 6 && "top-1/2 left-1/2 -translate-x-[67%] -translate-y-[29%] rotate-180 mix-blend-hard-light",
          )}
        >
          <GradientSvg
            className="w-full h-full"
            isHovered={isHovered}
            colors={colors}
            gradientId={`${gradientId}-${index}`}
          />
        </div>
      ))}
    </>
  );
}

export interface LiquidButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** WUBRG palette by default. Use MANA_U / MANA_R / MANA_G for themed buttons. */
  palette?: Colors;
  /** Optional trailing glyph rendered after the children (e.g. arrow). */
  trailing?: React.ReactNode;
}

let gradientCounter = 0;

export default function LiquidButton({
  palette = MANA_WUBRG,
  trailing,
  children,
  className,
  onMouseEnter,
  onMouseLeave,
  ...rest
}: LiquidButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  // Each button mount gets its own gradient id so React doesn't collide
  // when the same button is rendered in multiple spots on a page.
  const [gradientId] = useState(() => `liquid-grad-${++gradientCounter}`);

  return (
    <span
      className={cn(
        "relative inline-block h-[2.85em] group rounded-lg isolate",
        "bg-black border-2 border-white/90",
        className,
      )}
    >
      {/* Invisible sizer — drives the wrapper's intrinsic width from the
          real content. Without this, every decorative layer is absolutely
          positioned and the wrapper collapses to zero width. Clicks pass
          through to the absolute button below because this sizer is
          pointer-events-none. */}
      <span
        aria-hidden
        className="invisible relative block h-full px-6 whitespace-nowrap pointer-events-none"
      >
        <span className="flex h-full items-center justify-center gap-2 font-semibold tracking-wide">
          {children}
          {trailing}
        </span>
      </span>

      {/* Outer glow halo (blurred). Sits BEHIND the button surface. */}
      <span
        aria-hidden
        className="absolute -inset-y-[10%] inset-x-0 filter blur-[19px] opacity-70 pointer-events-none"
      >
        <span className="absolute inset-0 rounded-lg bg-[#d9d9d9] filter blur-[6.5px]" />
        <span className="relative block w-full h-full overflow-hidden rounded-lg [contain:paint]">
          <Liquid isHovered={isHovered} colors={palette} gradientId={`${gradientId}-halo`} />
        </span>
      </span>

      {/* Inner shadow pane */}
      <span
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[40%] w-[92%] h-[112%] rounded-lg bg-[#010108] filter blur-[7.3px] pointer-events-none"
      />

      {/* The actual rendered button surface. MUST be absolute — if it
          claims normal flow it stacks below the invisible sizer and the
          gradient bleeds beneath the text. */}
      <span className="absolute inset-0 overflow-hidden rounded-lg [contain:paint]">
        <span aria-hidden className="absolute inset-0 rounded-lg bg-[#d9d9d9]" />
        <span aria-hidden className="absolute inset-0 rounded-lg bg-black" />
        <Liquid isHovered={isHovered} colors={palette} gradientId={`${gradientId}-face`} />
        {[1, 2, 3, 4, 5].map((i) => (
          <span
            key={i}
            aria-hidden
            className={cn(
              "absolute inset-0 rounded-lg border-solid border-[3px] border-white/0 mix-blend-overlay filter",
              i <= 2 ? "blur-[3px]" : i === 3 ? "blur-[5px]" : "blur-[4px]",
            )}
          />
        ))}
        <span
          aria-hidden
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[40%] w-[70%] h-[42%] rounded-lg filter blur-[15px] bg-black/50"
        />
      </span>

      {/* Hover overlay — a very light transparent wash that fades in on
          hover. Sits above the gradient layers but below the text button,
          so it brightens the surface without blocking interaction. */}
      <span
        aria-hidden
        className={cn(
          "absolute inset-0 rounded-lg pointer-events-none transition-opacity duration-300",
          "bg-white/10",
          isHovered ? "opacity-100" : "opacity-0",
        )}
      />

      {/* The interactive button. Transparent; clicks fall through to children. */}
      <button
        type="button"
        {...rest}
        onMouseEnter={(e) => { setIsHovered(true); onMouseEnter?.(e); }}
        onMouseLeave={(e) => { setIsHovered(false); onMouseLeave?.(e); }}
        className="absolute inset-0 rounded-lg bg-transparent cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span
          className={cn(
            "flex items-center justify-center gap-2 px-6 h-full",
            "text-white font-semibold tracking-wide whitespace-nowrap",
            // Soft dark shadow beneath each glyph so the text reads as
            // cleanly bright on top of the swirling gradient rather than
            // getting visually mixed into the reds/greens behind it.
            "[text-shadow:0_1px_2px_rgba(0,0,0,0.8),0_0_8px_rgba(0,0,0,0.5)]",
            "drop-shadow-[0_1px_1px_rgba(0,0,0,0.6)]",
            "transition-colors",
          )}
        >
          {children}
          {trailing}
        </span>
      </button>
    </span>
  );
}
